import React, {Fragment, useEffect, useState} from 'react';
import {
    FormLabel,
} from '@mui/material';
import {t} from 'i18next';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import {useSelector} from 'react-redux';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import {user} from '../../../../store/reducers/user';
import Formatter from '../../../../util/formatter';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import AgyleAccordion from '../../../../components/ui/accordion/agyleAccordion';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import Tooltip from '@mui/material/Tooltip';
import uuidv4 from 'uuid/v4';
import Notes from '../../../../util/notes';


const useStyles = makeStyles((theme) => ({
    divider: {
        width: '100%',
        alignItems: 'center',
        padding: "10px",
        marginBottom: '10px',
    },

    mandatory: {
        fontWeight: 'bold',
        color: '#e06666',
    },

    contentStyle:{
        height: '800px',
    },

    scoreBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: 35,
        padding: 10,
        borderRadius: 5,
        backgroundColor: theme.palette.designSystem.colors.green[700],
        fontWeight: 700,
        color: '#000000',
    }
}));

const activityItems = [
    {
        id: "vocalisation",
        field: "vocalisation",
        label: "1. Vocalisation",
        description: "Eg Whimpering, groaning, crying",
    },
    {
        id: "facialExpression",
        field: "facialExpression",
        label: "2. Facial Expression",
        description: "Eg looking tense, frowning, grimacing, looking frightened",
    },
    {
        id: "changeInBody",
        field: "changeInBody",
        label: "3. Change in body language",
        description: "Eg Fidgeting, rocking, guarding part of body, withdrawn",
    },
    {
        id: "behavioural",
        field: "behavioural",
        label: "4. Behavioural change",
        description: "Increased confusion, refusing to eat, alteration in usual patterns",
    },
    {
        id: "physiological",
        field: "physiological",
        label: "5. Physiological change",
        description: "Eg Temperature, pulse or blood pressure outside normal limits, perspiring, flushing or pallor",
    },
    {
        id: "physical",
        field: "physical",
        label: "6. Physical changes",
        description: "Eg Skin tears, pressure areas, arthritis, contractures, previous injuries",
    },
]

const AbbeyPainScaleForm = (props) => {
    const classes = useStyles();
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    const [abbeyForm, setAbbeyForm] = useState({
        familyName: props.patient?.patient?.familyName ? props.patient?.patient?.familyName : "",
        firstName: props.patient?.patient?.firstName ? props.patient.patient.firstName: "",

        dateAndTime: moment(),

        vocalisation: null,
        facialExpression: null,
        changeInBody: null,
        behavioural: null,
        physiological: null,
        physical: null,

        total: null,
        initials1: tester || "",
        typeOfPain: "",
        cancelled: false,
    })

    const [accordionExpandedState, setAccordionExpandedState] = useState({
        patientInfo: true,
        date: true,
        questions: true,
        typeOfPain: true,
    });

    const handleChangeTextField = (event) => {
        const {name, value} = event.target;
        setAbbeyForm(pr => ({
            ...pr,
            [name]: value,
        }));
    };

    const handleChangeAccordion = (param) => {
        setAccordionExpandedState((pr) =>
            ({
                ...pr,
                [param]:!pr[param]
            }));
    };

    const isSubmitDisabled = () => {

        let submitDisabled = false;

        const requiredFields = [
            abbeyForm.dateAndTime,
            abbeyForm.vocalisation,
            abbeyForm.facialExpression,
            abbeyForm.changeInBody,
            abbeyForm.behavioural,
            abbeyForm.physiological,
            abbeyForm.physical,
            abbeyForm.typeOfPain,
        ];

        if (requiredFields.some(field => field === null) || requiredFields.some(field => field === "")) {
            submitDisabled = true;
        }
        return submitDisabled;
    };

    useEffect(() => {
        if (props.onSubmitFunction) {
            props.onSubmitFunction(handleSaveForm);
        }
        if (props.onDisableSubmit) {
            props.onDisableSubmit(isSubmitDisabled() || props.disableSave);
        }
    }, [abbeyForm, props.disableSave]);

    const handleSaveForm = () => {
        const objectToSave = {
            ...abbeyForm,
            timeStamp: moment().toISOString(),
            by: tester,
            id: uuidv4(),
        }

        const list = props.patient.studies?.abbeyPainScaleForm || [];

        let dataArray = [{
            name: "studies.abbeyPainScaleForm",
            value: [...list, objectToSave],
            noteType: "studies.abbeyPainScale",
            noteToAdd: Notes.getAbbeyPainScaleNote(objectToSave),
        }]

        props.onChange(dataArray);
    };

    useEffect(() => {

        setAbbeyForm(pr => ({
            ...pr,
            total: Number(abbeyForm.vocalisation) + Number(abbeyForm.facialExpression) + Number(abbeyForm.changeInBody) + Number(abbeyForm.behavioural) + Number(abbeyForm.physiological) + Number(abbeyForm.physical),
        }))

    },[abbeyForm.vocalisation, abbeyForm.facialExpression, abbeyForm.changeInBody, abbeyForm.behavioural, abbeyForm.physiological, abbeyForm.physical])

    const renderActivityItems = () => {
        return activityItems.map(({id, field, label, description})=> {
            return (
                <Fragment key={id}>
                    <>
                        <Grid item xs={5}>
                            <div>{t(label)}</div>
                        </Grid>
                        <Grid item xs={1}>
                            {description &&
                                <>
                                <Tooltip title={t(description)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="17" viewBox="0 0 18 17" fill="none">
                                        <path d="M8.95832 13.4998C9.24999 13.4998 9.49652 13.3991 9.69791 13.1978C9.8993 12.9964 9.99999 12.7498 9.99999 12.4582C9.99999 12.1665 9.8993 11.92 9.69791 11.7186C9.49652 11.5172 9.24999 11.4165 8.95832 11.4165C8.66666 11.4165 8.42013 11.5172 8.21874 11.7186C8.01735 11.92 7.91666 12.1665 7.91666 12.4582C7.91666 12.7498 8.01735 12.9964 8.21874 13.1978C8.42013 13.3991 8.66666 13.4998 8.95832 13.4998ZM8.20832 10.2915H9.74999C9.74999 9.83317 9.80207 9.47206 9.90624 9.20817C10.0104 8.94428 10.3055 8.58317 10.7917 8.12484C11.1528 7.76373 11.4375 7.41998 11.6458 7.09359C11.8542 6.7672 11.9583 6.37484 11.9583 5.9165C11.9583 5.13873 11.6736 4.5415 11.1042 4.12484C10.5347 3.70817 9.8611 3.49984 9.08332 3.49984C8.29166 3.49984 7.64929 3.70817 7.15624 4.12484C6.66318 4.5415 6.31943 5.0415 6.12499 5.62484L7.49999 6.1665C7.56943 5.9165 7.72568 5.64567 7.96874 5.354C8.21179 5.06234 8.58332 4.9165 9.08332 4.9165C9.52777 4.9165 9.8611 5.03803 10.0833 5.28109C10.3055 5.52414 10.4167 5.7915 10.4167 6.08317C10.4167 6.36095 10.3333 6.62137 10.1667 6.86442C9.99999 7.10748 9.79166 7.33317 9.54166 7.5415C8.93055 8.08317 8.55555 8.49289 8.41666 8.77067C8.27777 9.04845 8.20832 9.55539 8.20832 10.2915ZM8.99999 16.8332C7.84721 16.8332 6.76388 16.6144 5.74999 16.1769C4.7361 15.7394 3.85416 15.1457 3.10416 14.3957C2.35416 13.6457 1.76041 12.7637 1.32291 11.7498C0.885406 10.7359 0.666656 9.65261 0.666656 8.49984C0.666656 7.34706 0.885406 6.26373 1.32291 5.24984C1.76041 4.23595 2.35416 3.354 3.10416 2.604C3.85416 1.854 4.7361 1.26025 5.74999 0.822754C6.76388 0.385254 7.84721 0.166504 8.99999 0.166504C10.1528 0.166504 11.2361 0.385254 12.25 0.822754C13.2639 1.26025 14.1458 1.854 14.8958 2.604C15.6458 3.354 16.2396 4.23595 16.6771 5.24984C17.1146 6.26373 17.3333 7.34706 17.3333 8.49984C17.3333 9.65261 17.1146 10.7359 16.6771 11.7498C16.2396 12.7637 15.6458 13.6457 14.8958 14.3957C14.1458 15.1457 13.2639 15.7394 12.25 16.1769C11.2361 16.6144 10.1528 16.8332 8.99999 16.8332ZM8.99999 15.1665C10.8611 15.1665 12.4375 14.5207 13.7292 13.229C15.0208 11.9373 15.6667 10.3609 15.6667 8.49984C15.6667 6.63873 15.0208 5.06234 13.7292 3.77067C12.4375 2.479 10.8611 1.83317 8.99999 1.83317C7.13888 1.83317 5.56249 2.479 4.27082 3.77067C2.97916 5.06234 2.33332 6.63873 2.33332 8.49984C2.33332 10.3609 2.97916 11.9373 4.27082 13.229C5.56249 14.5207 7.13888 15.1665 8.99999 15.1665Z" fill="#ACAFB3"/>
                                    </svg>
                                </Tooltip>
                                </>
                            }
                        </Grid>

                        <Grid item xs={4}>
                            <AgyleBlockSwitch
                                onChange={
                                    (value) => {
                                        setAbbeyForm(pr => ({
                                            ...pr,
                                            [`${field}`]: value,
                                        }))
                                    }
                                }
                                value={abbeyForm[`${field}`]}
                                nullState
                                fourCases
                                valueObject={{
                                    true: '0',
                                    null: '1',
                                    alt: '2',
                                    false: '3',
                                }}
                                customLabels={{
                                    yes: t('None'),
                                    empty: t('Mild'),
                                    alternative: t('Moderate'),
                                    no: t('Severe'),
                                }}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <div className={classes.scoreBox}>
                                Score: {abbeyForm[`${field}`]}
                            </div>
                        </Grid>
                    </>
                </Fragment>
            )
        })
    };

    return (
        <>
            <AgyleAccordion
                expanded={accordionExpandedState.patientInfo}
                onChange={()=>handleChangeAccordion("patientInfo")}
                label="Patient Info"
            >
                <Grid container spacing={2}>
                    <Grid item xs={4}>
                        <FormLabel>{t("Surname")}</FormLabel>
                    </Grid>
                    <Grid item xs={4}>
                        <FormLabel>{t("Name")}</FormLabel>
                    </Grid>
                    <Grid item xs={4}/>
                    <Grid item xs={4}>
                        <AgyleTextField
                            fullWidth
                            name="familyName"
                            maxRows={25}
                            debounce
                            disabled
                            onChange={handleChangeTextField}
                            value={abbeyForm.familyName}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <AgyleTextField
                            fullWidth
                            name="firstName"
                            maxRows={25}
                            debounce
                            disabled
                            onChange={handleChangeTextField}
                            value={abbeyForm.firstName}
                            variant="outlined"
                        />
                    </Grid>
                </Grid>
            </AgyleAccordion>
            <AgyleAccordion
                expanded={accordionExpandedState.date}
                onChange={()=>handleChangeAccordion("date")}
                label="Date"
            >
                <Grid container spacing={2}>

                    <Grid item xs={4}>
                        <FormLabel> {t('Date and time')} </FormLabel>
                    </Grid>
                    <Grid item xs={4}>
                        <FormLabel> {t('Inicials')} </FormLabel>
                    </Grid>
                    <Grid item xs={4}/>

                    <Grid item xs={4}>
                        <AgyleDatePicker
                            type="date"
                            value={abbeyForm.dateAndTime}
                            onChange={(param) => {setAbbeyForm((pr) =>
                                ({
                                    ...pr,
                                    dateAndTime: param
                                }))}}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <AgyleTextField
                            fullWidth
                            name="initials1"
                            maxRows={25}
                            debounce
                            disabled
                            onChange={handleChangeTextField}
                            value={abbeyForm.initials1}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={4}/>
                </Grid>
            </AgyleAccordion>
            <AgyleAccordion
                expanded={accordionExpandedState.questions}
                onChange={()=>handleChangeAccordion("questions")}
                label="Questions"
            >
                <Grid container spacing={2} sx={{ marginTop: 2 }}>
                    {renderActivityItems()}

                    <Divider className={classes.divider}/>

                    <Grid item xs={10}/>
                    <Grid item xs={2}>
                        <div className={classes.scoreBox}>
                            Total Score: {abbeyForm.total}
                        </div>
                    </Grid>
                </Grid>
            </AgyleAccordion>
            <AgyleAccordion
                expanded={accordionExpandedState.typeOfPain}
                onChange={()=>handleChangeAccordion("typeOfPain")}
                label="Type Of Pain"
            >
                <Grid container spacing={2} sx={{ marginTop: 2 }}>
                    <Grid item xs={9}>
                        <FormLabel>{t("Tick the box which matches the type of pain")}</FormLabel>
                    </Grid>
                    <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <AgyleBlockSwitch
                            onChange={event => setAbbeyForm({...abbeyForm, typeOfPain: event})}
                            value={abbeyForm.typeOfPain}
                            exclusive
                            fullWidth
                            nullState
                            valueObject={{
                                true: 'Chronic',
                                null: 'Acute',
                                false: 'Acute on chronic',
                            }}
                            customLabels={{
                                yes: t('Chronic'),
                                empty: t('Acute'),
                                no: t('Acute On Chronic'),
                            }}
                        />
                    </Grid>
                </Grid>
            </AgyleAccordion>
        </>
    )
}

export default AbbeyPainScaleForm;